import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, Users, Star, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';
import RelatedServices from './RelatedServices';

const WealthManagementServices = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.wealthManagementServices} faqJsonLd={faqSchemas.wealthManagementServices} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  PREMIUM WEALTH ADVISORY
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Wealth Management Services
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  For HNI & UHNI Clients
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Comprehensive wealth management for High Net Worth Individuals. From portfolio management 
                to estate planning, we offer bespoke financial solutions backed by 25+ years of expertise 
                and SEBI registration.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Schedule Private Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  Explore Services
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/6801872/pexels-photo-6801872.jpeg?w=800&q=85" 
                  alt="Wealth management services for HNI India - Premium portfolio management, PMS, AIF, and estate planning for High Net Worth Individuals with ₹500 Cr+ AUM"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                25+ Years
              </div>
              <div className="text-gray-600">Wealth Management Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                ₹500 Cr+
              </div>
              <div className="text-gray-600">Assets Under Management</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                100+
              </div>
              <div className="text-gray-600">HNI Families</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                SEBI
              </div>
              <div className="text-gray-600">Registered RIA</div>
            </div>
          </div>
        </div>
      </section>

      {/* HNI Wealth Challenges */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Unique Wealth Management Challenges We Solve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sophisticated solutions for complex financial needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Wealth Preservation</h3>
              <p className="text-gray-600 leading-relaxed">
                Protect your accumulated wealth from market volatility, inflation, and taxation through 
                strategic asset allocation across multiple asset classes and geographies.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Wealth Growth</h3>
              <p className="text-gray-600 leading-relaxed">
                Generate alpha through exclusive investment opportunities including PMS, AIF, structured 
                products, and alternative investments not accessible to retail investors.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Wealth Transfer</h3>
              <p className="text-gray-600 leading-relaxed">
                Plan seamless wealth transfer to next generation through estate planning, trusts, wills, 
                and tax-efficient succession strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Wealth Management Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end financial services for holistic wealth management
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Portfolio Management Services (PMS)",
                description: "Customized equity and multi-asset portfolios with dedicated relationship managers and personalized strategies",
                minInvestment: "₹50 Lakhs"
              },
              {
                title: "Alternative Investment Funds (AIF)",
                description: "Category I, II, III AIFs including multi-asset, debt, and hedge fund strategies for portfolio diversification",
                minInvestment: "₹1 Crore"
              },
              {
                title: "Structured Products",
                description: "Capital protection with market-linked returns, principal-protected notes, and yield-enhancement products",
                minInvestment: "₹25 Lakhs"
              },
              {
                title: "Real Estate Advisory",
                description: "Commercial real estate, REITs, land banking, and property investment guidance for wealth diversification",
                minInvestment: "₹50 Lakhs"
              },
              {
                title: "International Investments",
                description: "US stocks, global mutual funds, and international portfolio management under LRS for global diversification",
                minInvestment: "$10,000"
              },
              {
                title: "Tax & Estate Planning",
                description: "Comprehensive tax optimization, will drafting, trust formation, and succession planning services",
                minInvestment: "Consultation-based"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-sm font-semibold text-blue-600">Min Investment: {service.minInvestment}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Explore All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Wealth Management Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Wealth Management Approach
            </h2>
            <p className="text-xl text-gray-600">
              Personalized, research-driven wealth management
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Wealth Discovery",
                description: "Comprehensive assessment of your assets, liabilities, income sources, and financial goals"
              },
              {
                step: "02",
                title: "Strategy Design",
                description: "Customized wealth management strategy with optimal asset allocation and risk management"
              },
              {
                step: "03",
                title: "Implementation",
                description: "Execute strategy through best-in-class investment products and service providers"
              },
              {
                step: "04",
                title: "Ongoing Management",
                description: "Continuous monitoring, quarterly reviews, tax planning, and portfolio rebalancing"
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our HNI Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Trust built through exceptional service
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Arun Malhotra",
                location: "Mumbai",
                testimonial: "Managing ₹15 crore portfolio with Rumtek. Their PMS has consistently delivered 18%+ returns. Professional team understands HNI needs perfectly. Best wealth management in India!"
              },
              {
                name: "Kavita Sharma",
                location: "Delhi",
                testimonial: "Excellent estate planning and succession advisory. They helped structure my ₹20 crore wealth efficiently across trusts and investments. Tax savings of ₹60L+ annually!"
              },
              {
                name: "Rajiv Patel",
                location: "Ahmedabad",
                testimonial: "Sophisticated international portfolio management. From US stocks to global AIFs, they provide access to premium opportunities. Grown my wealth from ₹5 Cr to ₹12 Cr in 6 years!"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.testimonial}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Wealth Management FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions from HNI clients
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "What is the minimum investment for wealth management services?",
                answer: "Our comprehensive wealth management services are designed for HNI clients with investable assets of ₹50 lakhs or more. This threshold allows us to provide sophisticated strategies including PMS, AIFs, and structured products."
              },
              {
                question: "How do you charge for wealth management services?",
                answer: "We follow a transparent fee structure: PMS charges 2-2.5% of AUM, AIFs charge as per fund structure, and advisory services are charged based on portfolio complexity. No hidden costs or commissions - complete transparency."
              },
              {
                question: "What makes your wealth management different?",
                answer: "25+ years of experience, SEBI registration, personalized service with dedicated relationship managers, access to exclusive investment opportunities, comprehensive tax planning, and holistic wealth management covering all aspects of your finances."
              },
              {
                question: "Do you offer international investment services?",
                answer: "Yes! We provide US market investments under LRS, global mutual funds, international real estate advisory, and offshore portfolio management for clients looking to diversify geographically."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['pms', 'equity', 'bonds', 'nri', 'retirement', 'calculator']} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Experience Premium Wealth Management
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join India's leading HNI families who trust Rumtek for sophisticated wealth management. 
            Schedule your private consultation today and discover how we can help grow and preserve your wealth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Schedule Private Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/services"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WealthManagementServices;